import "./Graph.css";
import { useLayoutEffect, useMemo, useRef } from "react";
import { STATUS, StatusPill } from "./Status";
import type { Issue } from "../lib/tasks";
import { isWorking } from "../lib/tasks";
import { tx } from "../i18n";

/* ══ 일감 › 그래프(t-in-port-work-graph) ══
   선행(dependsOn)과 상하위(parentId)를 노드-선 그림으로 본다. 차트가 아니라 그림이라 공통 차트 모듈 대상이 아니다.
   **두 축은 다른 축이다**(AGENTS.md 「parentId 와 dependsOn 은 다른 축」) — 선행은 실선 + 화살표, 하위는 점선.
   배치는 순수 함수 둘(`graphView` 무엇을 그리나 · `layoutGraph` 어디에)이 정하고 그리는 쪽은 받아 그리기만 한다. */

export type GraphOpts = { foldKids: boolean; expanded: Set<string>; hideDone: boolean };
export type GEdge = { from: string; to: string; kind: "dep" | "child" };
export type GraphView = { nodes: Issue[]; edges: GEdge[]; folded: number; hiddenDone: number };

const closed = (t: Issue) => t.status === "done" || t.status === "cancelled";

/** 무엇을 그리나. 접힌 하위는 **보이는 조상**으로 선을 옮겨 붙인다 — 선행이 접힌 잎을 가리켜도 「무엇을 기다리나」가
 *  사라지지 않게. 숨긴 완료 노드로 가는 선행은 지운다(끝난 선행은 더는 기다릴 것이 아니다). */
export function graphView(all: Issue[], o: GraphOpts): GraphView {
  const byId = new Map(all.map((t) => [t.id, t]));
  let hiddenDone = 0;
  const shown = new Map<string, Issue>();
  for (const t of all) {
    if (o.hideDone && closed(t)) hiddenDone++;
    else shown.set(t.id, t);
  }
  // 이 노드를 그릴 자리 — 접힌 조상이 있으면 가장 바깥의 접힌 조상(=보이는 노드).
  const seat = (id: string): string | null => {
    let cur = byId.get(id);
    let at: string | null = cur && shown.has(cur.id) ? cur.id : null;
    let guard = 0;
    while (cur?.parentId && guard++ < 50) {
      const p = byId.get(cur.parentId);
      if (!p) break;
      if (o.foldKids && !o.expanded.has(p.id) && shown.has(p.id)) at = p.id;
      cur = p;
    }
    return at;
  };
  const nodes: Issue[] = [];
  let folded = 0;
  for (const t of shown.values()) {
    if (seat(t.id) === t.id) nodes.push(t);
    else folded++;
  }
  const keep = new Set(nodes.map((t) => t.id));
  const seen = new Set<string>();
  const edges: GEdge[] = [];
  const add = (e: GEdge) => {
    const k = `${e.kind}|${e.from}|${e.to}`;
    if (e.from === e.to || seen.has(k) || !keep.has(e.from) || !keep.has(e.to)) return;
    seen.add(k);
    edges.push(e);
  };
  for (const t of shown.values()) {
    const to = seat(t.id);
    if (!to) continue;
    for (const d of t.dependsOn ?? []) {
      const from = seat(d);
      if (from) add({ from, to, kind: "dep" });
    }
    if (t.parentId && to === t.id) add({ from: t.parentId, to, kind: "child" });
  }
  return { nodes, edges, folded, hiddenDone };
}

export const NODE_W = 220;
export const NODE_H = 72;
const GAP_X = 72;
const GAP_Y = 16;

export type Placed = { t: Issue; x: number; y: number };

/** 어디에. 층 = 들어오는 선을 따라간 가장 긴 길이(선행이 늘 왼쪽에 선다). 고리가 있으면 층 수 상한에서 멈춘다 —
 *  고리는 서버가 막지만, 그림이 무한히 돌지 않게 여기서도 끊는다. 한 층 안의 순서는 받은 순서(서버 줄 순서)다.
 *  노드가 많아도(100+) 층은 가로로, 한 층은 세로로 늘어날 뿐이고 바탕이 양쪽으로 구른다. */
export function layoutGraph(v: GraphView): { placed: Placed[]; w: number; h: number } {
  const layer = new Map(v.nodes.map((t) => [t.id, 0]));
  const cap = v.nodes.length;
  for (let pass = 0; pass < cap; pass++) {
    let moved = false;
    for (const e of v.edges) {
      const want = (layer.get(e.from) ?? 0) + 1;
      if (want > (layer.get(e.to) ?? 0) && want <= cap) {
        layer.set(e.to, want);
        moved = true;
      }
    }
    if (!moved) break;
  }
  const rows = new Map<number, number>();
  const placed = v.nodes.map((t) => {
    const l = layer.get(t.id) ?? 0;
    const r = rows.get(l) ?? 0;
    rows.set(l, r + 1);
    return { t, x: l * (NODE_W + GAP_X), y: r * (NODE_H + GAP_Y) };
  });
  const cols = Math.max(0, ...placed.map((p) => p.x)) + NODE_W;
  const tall = Math.max(0, ...placed.map((p) => p.y)) + NODE_H;
  return { placed, w: placed.length ? cols : 0, h: placed.length ? tall : 0 };
}

export function GraphCanvas({
  view,
  zoom,
  focus,
  foldKids,
  expanded,
  onOpen,
  onToggle,
}: {
  view: GraphView;
  zoom: number;
  focus: string | null;
  foldKids: boolean;
  expanded: Set<string>;
  onOpen: (id: string) => void;
  onToggle: (id: string) => void;
}) {
  const { placed, w, h } = useMemo(() => layoutGraph(view), [view]);
  const at = useMemo(() => new Map(placed.map((p) => [p.t.id, p])), [placed]);
  const preds = useMemo(() => {
    const n = new Map<string, number>();
    for (const e of view.edges) if (e.kind === "dep") n.set(e.to, (n.get(e.to) ?? 0) + 1);
    return n;
  }, [view]);
  const box = useRef<HTMLDivElement>(null);

  // 찾은 노드를 화면 가운데로 — 배율을 곱한 자리로 굴린다.
  useLayoutEffect(() => {
    const p = focus ? at.get(focus) : null;
    const el = box.current;
    if (!p || !el) return;
    el.scrollTo({ left: p.x * zoom - el.clientWidth / 2 + (NODE_W * zoom) / 2, top: p.y * zoom - el.clientHeight / 2, behavior: "smooth" });
  }, [focus, at, zoom]);

  return (
    <div className="graph" ref={box}>
      <div className="graph__stage" style={{ width: w * zoom, height: h * zoom }}>
        <div className="graph__plane" style={{ width: w, height: h, transform: `scale(${zoom})` }}>
          <svg className="graph__edges" width={w} height={h} aria-hidden="true">
            <defs>
              <marker id="graph-arrow" viewBox="0 0 8 8" refX="8" refY="4" markerWidth="8" markerHeight="8" orient="auto">
                <path d="M0 0 8 4 0 8Z" className="graph__arrow" />
              </marker>
            </defs>
            {view.edges.map((e) => {
              const a = at.get(e.from);
              const b = at.get(e.to);
              if (!a || !b) return null;
              const x1 = a.x + NODE_W;
              const y1 = a.y + NODE_H / 2;
              const x2 = b.x;
              const y2 = b.y + NODE_H / 2;
              const mx = (x1 + x2) / 2;
              return (
                <path
                  key={`${e.kind}|${e.from}|${e.to}`}
                  className={`graph__edge is-${e.kind}`}
                  d={`M${x1} ${y1} C${mx} ${y1} ${mx} ${y2} ${x2} ${y2}`}
                  markerEnd={e.kind === "dep" ? "url(#graph-arrow)" : undefined}
                />
              );
            })}
          </svg>
          {placed.map(({ t, x, y }) => {
            const tone = STATUS[t.status].tone;
            const canFold = foldKids && t.childCount > 0;
            const isOpen = expanded.has(t.id);
            const n = preds.get(t.id) ?? 0;
            return (
              <div key={t.id} className={`graph__node is-${tone}${focus === t.id ? " is-focus" : ""}`} style={{ left: x, top: y, width: NODE_W, height: NODE_H }}>
                <button type="button" className="graph__open" onClick={() => onOpen(t.id)} title={t.title}>
                  <span className="graph__title">{t.title}</span>
                  <span className="graph__meta">
                    <StatusPill status={t.status} working={isWorking(t)} />
                    <code className="muted">{t.id}</code>
                  </span>
                  <span className="graph__meta caption">
                    {t.childCount > 0 ? <span>{tx("graph.kids", { done: t.doneChildCount, n: t.childCount })}</span> : null}
                    {n ? <span title={tx("graph.predsTip")}>{tx("graph.preds", { n })}</span> : null}
                  </span>
                </button>
                {canFold ? (
                  <button
                    type="button"
                    className="graph__fold"
                    aria-expanded={isOpen}
                    aria-label={tx(isOpen ? "graph.collapse" : "graph.expand", { title: t.title })}
                    title={tx(isOpen ? "graph.collapse" : "graph.expand", { title: t.title })}
                    onClick={() => onToggle(t.id)}
                  >
                    {isOpen ? "−" : "+"}
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
